import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clover, ArrowLeft, User, Lock, Crown, Loader2, CalendarClock, Check, ShieldOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, setUser, loading, refresh } = useAuth();

  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [action, setAction] = useState(false);

  useEffect(() => { if (user) setName(user.name || ""); }, [user]);

  const loadSub = async () => {
    setSubLoading(true);
    try {
      const { data } = await api.get("/account/subscription");
      setSub(data);
    } catch {
      setSub(null);
    } finally {
      setSubLoading(false);
    }
  };
  useEffect(() => { if (user) loadSub(); }, [user]);

  if (!loading && !user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC] p-6 text-center">
        <p className="font-bold text-slate-700 mb-4">Connectez-vous pour accéder à votre compte.</p>
        <Button onClick={() => navigate("/")} className="rounded-full font-bold text-white bg-[#0052CC]" data-testid="account-go-home">Retour à l'accueil</Button>
      </div>
    );
  }
  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><Loader2 className="w-8 h-8 animate-spin text-[#0052CC]" /></div>;
  }

  const saveName = async () => {
    setSavingName(true);
    try {
      const { data } = await api.put("/account/profile", { name });
      setUser(data);
      toast.success("Profil mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingName(false);
    }
  };

  const savePwd = async () => {
    setSavingPwd(true);
    try {
      await api.post("/account/password", pwd);
      setPwd({ current_password: "", new_password: "" });
      toast.success("Mot de passe modifié");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingPwd(false);
    }
  };

  const upgrade = async () => {
    setAction(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setAction(false);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Annuler votre abonnement Premium ? Vous gardez l'accès jusqu'à la fin de la période payée.")) return;
    setAction(true);
    try {
      await api.post("/account/cancel-subscription");
      toast.success("Abonnement annulé (actif jusqu'à la fin de période)");
      await loadSub();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAction(false); }
  };

  const resumeSub = async () => {
    setAction(true);
    try {
      await api.post("/account/resume-subscription");
      toast.success("Abonnement réactivé");
      await loadSub();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAction(false); }
  };

  const s = sub?.subscription;
  const periodEnd = s?.current_period_end ? new Date(s.current_period_end * 1000).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-50 bg-[#0B132B] text-white px-4 sm:px-6 py-3.5 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-[#0B132B] bg-white shadow"><Clover className="w-5 h-5" /></div>
          <div className="leading-none">
            <div className="font-black text-lg tracking-tight">Mon compte</div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-white/50">ProbaLoto</div>
          </div>
        </div>
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm font-bold text-white/80 hover:text-white" data-testid="account-back-home">
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Profile */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="account-profile-card">
          <div className="flex items-center gap-4 mb-5">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-14 h-14 rounded-full border" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#0052CC] text-white flex items-center justify-center text-xl font-black">{(user.name || user.email)[0].toUpperCase()}</div>
            )}
            <div>
              <div className="font-black text-lg text-slate-900">{user.name}</div>
              <div className="text-sm text-slate-500">{user.email}</div>
            </div>
          </div>
          <label className="text-xs font-bold uppercase text-slate-400 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Nom affiché</label>
          <div className="flex flex-col sm:flex-row gap-2 mt-1">
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl flex-1" data-testid="account-name-input" />
            <Button onClick={saveName} disabled={savingName} className="rounded-full font-bold text-white bg-[#0052CC] hover:bg-[#003B80]" data-testid="account-save-name">
              {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        </div>

        {/* Password (email accounts only) */}
        {user.auth_provider === "email" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="account-password-card">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4"><Lock className="w-5 h-5 text-[#0052CC]" /> Changer le mot de passe</h3>
            <Input type="password" placeholder="Mot de passe actuel" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="rounded-xl mb-3" data-testid="account-current-password" />
            <Input type="password" placeholder="Nouveau mot de passe (min. 6 caractères)" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="rounded-xl mb-3" data-testid="account-new-password" />
            <Button onClick={savePwd} disabled={savingPwd || !pwd.current_password || pwd.new_password.length < 6} className="rounded-full font-bold text-white bg-[#0052CC] hover:bg-[#003B80]" data-testid="account-save-password">
              {savingPwd ? <Loader2 className="w-4 h-4 animate-spin" /> : "Modifier le mot de passe"}
            </Button>
          </div>
        )}

        {/* Subscription */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="account-subscription-card">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4"><Crown className="w-5 h-5 text-[#EAB308]" /> Mon abonnement</h3>
          {subLoading ? (
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          ) : sub?.is_premium ? (
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 font-black text-xs uppercase tracking-wider px-3 py-1.5 rounded-full mb-4">
                <Crown className="w-4 h-4" /> Premium actif
              </div>
              {s?.cancel_at_period_end ? (
                <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 mb-4 text-sm text-rose-700 flex items-start gap-2" data-testid="account-cancel-notice">
                  <ShieldOff className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Abonnement annulé. Accès Premium jusqu'au <strong>{periodEnd || "la fin de période"}</strong>, sans renouvellement.</span>
                </div>
              ) : periodEnd ? (
                <p className="text-sm text-slate-600 mb-4 flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-slate-400" /> Prochain renouvellement le <strong>{periodEnd}</strong> (9,99 €).</p>
              ) : null}
              {s?.cancel_at_period_end ? (
                <Button onClick={resumeSub} disabled={action} className="rounded-full font-bold text-white bg-emerald-600 hover:bg-emerald-700" data-testid="account-resume-btn">
                  {action ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Réactiver l'abonnement</>}
                </Button>
              ) : (
                <Button onClick={cancelSub} disabled={action} variant="outline" className="rounded-full font-bold text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="account-cancel-btn">
                  {action ? <Loader2 className="w-4 h-4 animate-spin" /> : "Annuler mon abonnement"}
                </Button>
              )}
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-600 mb-4">Vous êtes sur l'offre <strong>Gratuite</strong> (1 grille par jeu et par jour).</p>
              <Button onClick={upgrade} disabled={action} className="rounded-full font-black text-[#0B132B] bg-[#FFB800] hover:bg-[#EAB308]" data-testid="account-upgrade-btn">
                {action ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Crown className="w-4 h-4 mr-1" /> Passer Premium — 9,99 €/mois</>}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
